import {
    createEntityAdapter,
    createSlice,
    PayloadAction,
} from '@reduxjs/toolkit';

import {TAppState} from 'store/config/types';

import {DRAGABBLE_FRAMES_SLICE} from './draggable-frames-slice-constants';
import {IDraggableFrameEntity, TDraggableFramesSliceState} from './draggable-frames-slice-types';

const getId = (type: IDraggableFrameEntity['type'], id?: IDraggableFrameEntity['id']) => `${type}_${id ?? 'no-id'}`;

const draggableFramesEntityAdapter = createEntityAdapter<IDraggableFrameEntity>({
    selectId: model => getId(model.type, model.id),
});

export const draggableFramesSlice = createSlice({
    initialState: {
        draggableFrames: draggableFramesEntityAdapter.getInitialState(),
        lastFrameId: 0,
    } as TDraggableFramesSliceState,
    name: DRAGABBLE_FRAMES_SLICE,
    reducers: {
        openDraggableFrame(state, {
            payload,
        }: PayloadAction<Omit<IDraggableFrameEntity, 'id'> & {id?: number}>) {
            const nextFrameId = state.lastFrameId + 1;
            draggableFramesEntityAdapter.upsertOne(state.draggableFrames, {
                ...payload,
                id: payload.id ?? nextFrameId,
                isOpen: payload.isOpen ?? true,
            });
            if (!payload.id) {
                state.lastFrameId = nextFrameId;
            }
        },
        destroyDraggableFrame(state, {payload}: PayloadAction<{
            type: IDraggableFrameEntity['type'];
            id?: number;
        }>) {
            const {type, id} = payload;
            draggableFramesEntityAdapter.removeOne(state.draggableFrames, getId(type, id));
        },
        closeDraggableFrame(state, {payload}: PayloadAction<{
            type: IDraggableFrameEntity['type'];
            id?: number;
        }>) {
            const {type, id} = payload;

            draggableFramesEntityAdapter.updateOne(state.draggableFrames, {
                changes: {isOpen: false},
                id: getId(type, id),
            });
        },
    },
});

const selectDraggableFramesSliceState = (state: TAppState) => state[DRAGABBLE_FRAMES_SLICE];

const draggableFramesAdapterSelectors = draggableFramesEntityAdapter.getSelectors(
    (state: TAppState) => selectDraggableFramesSliceState(state).draggableFrames,
);

export const draggableFramesSelectors = {
    ...draggableFramesAdapterSelectors,
    selectDraggableFrameEntityData: <T=any>(state: TAppState,
        type: IDraggableFrameEntity['type']) => (draggableFramesAdapterSelectors
        .selectById(state, type) as IDraggableFrameEntity<T>)?.data,
};

export const {
    reducer: draggableFramesReducer,
    actions: draggableFramesActions,
} = draggableFramesSlice;
